import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, TextField, Button, Typography, Box, Alert, CircularProgress } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import ApiService from '../services/ApiService'
import { validatePassword } from '../utils/validateForm'

const ResetPassword = () => {
  const navigate = useNavigate()
  const { token } = useParams()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verifyingToken, setVerifyingToken] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)

  useEffect(() => {
    const verifyToken = async () => {
      try {
        await ApiService.get(`/auth/verify-reset-token/${token}`)
        setTokenValid(true)
      } catch (error) {
        setTokenValid(false)
      } finally {
        setVerifyingToken(false)
      }
    }

    if (token) {
      verifyToken()
    } else {
      setVerifyingToken(false)
    }
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()

    let valid = true

    if (!validatePassword(password)) {
      setPasswordError('Invalid password. Minimum length 6.')
      valid = false
    } else {
      setPasswordError('')
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.')
      valid = false
    } else {
      setConfirmPasswordError('')
    }

    if (!valid) return

    setLoading(true)
    setError('')

    try {
      await ApiService.post(`/auth/reset-password/${token}`, { password })
      setSuccess(true)
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset password. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value)
    if (passwordError) {
      setPasswordError('')
    }
    if (error) {
      setError('')
    }
  }

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value)
    if (confirmPasswordError) {
      setConfirmPasswordError('')
    }
    if (error) {
      setError('')
    }
  }

  return (
    <Container
      maxWidth="xs"
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
    >
      <Helmet>
        <title>Treegress - Reset Password</title>
      </Helmet>
      <Box sx={{ border: '1px solid #C4C4C4', borderRadius: '8px', padding: '32px' }}>
        <Typography variant="h6" align="center" fontWeight="400" gutterBottom>
          Create New Password
        </Typography>

        {verifyingToken ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !tokenValid ? (
          <>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
              This password reset link is invalid. It may have already been used or has expired. If you still need to reset your password, please request a new link
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/login')}
                sx={{ width: '250px', textTransform: 'none' }}
              >
                Back to Login
              </Button>
            </Box>
          </>
        ) : success ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your password has been reset successfully! Redirecting to login...
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/login')}
                sx={{ width: '250px', textTransform: 'none' }}
              >
                Go to Login
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Please enter your new password below.
            </Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                label="New Password"
                fullWidth
                margin="normal"
                variant="outlined"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
                error={!!passwordError}
                helperText={passwordError + ' '}
                disabled={loading}
              />
              <TextField
                label="Confirm New Password"
                fullWidth
                margin="normal"
                variant="outlined"
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                error={!!confirmPasswordError}
                helperText={confirmPasswordError + ' '}
                disabled={loading}
                sx={{ mt: 0 }}
              />
              {error && <Typography color="error" sx={{ margin: '-16px 0 8px 14px', fontSize: '0.75rem' }}>{error}</Typography>}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={loading}
                  sx={{ width: '250px', textTransform: 'none' }}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Box>
            </form>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="text"
                color="secondary"
                onClick={() => navigate('/login')}
                sx={{ textTransform: 'none' }}
              >
                Back to Login
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  )
}

export default ResetPassword

